import { DownloadOutlined } from '@mui/icons-material'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material'
import React from 'react'
import { CSVLink } from 'react-csv'
import { useAppSelector } from 'store/hooks'

export interface LocationSummary {
  name: string
  employeeNo?: string
  dateRange: string
  wfoCount: number
  wfhCount: number
  leaveCount: number
}

interface LocationReportDisplayProps {
  data: LocationSummary[]
  isLoading: boolean
  // eslint-disable-next-line react/require-default-props
  dateRange?: string
}

const LocationReportDisplay: React.FC<LocationReportDisplayProps> = ({
  data,
  isLoading,
  dateRange,
}) => {
  const { isDarkMode } = useAppSelector(state => state.app)

  // Format data for CSV export
  const getFormattedExportData = () =>
    data.map(row => ({
      ...row,
      // Add a combined employee field for export
      employee: row.employeeNo
        ? `${row.name || ''}\n${row.employeeNo}`
        : row.name || '',
    }))

  // Headers for CSV export
  const csvHeaders = [
    { label: 'Employee', key: 'employee' },
    { label: 'WFO Days', key: 'wfoCount' },
    { label: 'WFH Days', key: 'wfhCount' },
    { label: 'Leave Days', key: 'leaveCount' },
    { label: 'Date Range', key: 'dateRange' },
  ]

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
        <CircularProgress size={40} />
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3 }}>
      {data.length > 0 ? (
        <>
          <Box
            alignItems="center"
            display={'flex'}
            justifyContent="space-between"
            mb={2}
            mt={0}
          >
            <Typography variant="h6">
              Location Summary Report {dateRange && `(${dateRange})`}
            </Typography>
            <Box alignItems="center" display="flex">
              <Typography variant="subtitle2">
                Download as excel file :&emsp;
              </Typography>
              <CSVLink
                data={getFormattedExportData()}
                filename={`Location Report - ${new Date()
                  .toLocaleString()
                  .split(', ')
                  .join(' - ')}.csv`}
                headers={csvHeaders}
              >
                <IconButton sx={{ p: 0 }}>
                  <DownloadOutlined />
                </IconButton>
              </CSVLink>
            </Box>
          </Box>
          <Paper
            sx={{
              border: '0.031rem solid',
              borderColor: isDarkMode
                ? 'rgba(255, 255, 255, 10%)'
                : 'rgba(0, 0, 0, 10%)',
            }}
            variant="outlined"
          >
            <TableContainer sx={{ maxHeight: 'calc(100vh - 150px)' }}>
              <Table
                stickyHeader
                sx={{
                  '& .MuiTableHead-root': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      th: {
                        backgroundColor: isDarkMode
                          ? 'rgba(81, 143, 175)'
                          : '#4fc3f7',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>WFO</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>WFH</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Leave</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Date Range
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${row.name}-${index}`}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: isDarkMode
                            ? 'rgba(42, 42, 42, 0.1)'
                            : 'rgba(242, 242, 242, 0.5)',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontSize: '0.9rem' }}>
                            {row.name}
                          </Typography>
                          {row.employeeNo && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                              }}
                            >
                              {row.employeeNo}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{row.wfoCount}</TableCell>
                      <TableCell>{row.wfhCount}</TableCell>
                      <TableCell>{row.leaveCount}</TableCell>
                      <TableCell>{row.dateRange}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid #bdbdbd',
            mt: '1.5rem',
            p: '1.5rem',
          }}
        >
          <Typography variant="h6">No Location Data Found</Typography>
        </Box>
      )}
    </Box>
  )
}

export default LocationReportDisplay
